import { Bell, CaretDown, Heart, MapPin } from '@phosphor-icons/react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { KuniriLogo } from '@/components/kuniri-logo'
import { formatNumber } from '@/lib/i18n/format'

import { useIsDesktop } from '../hooks/use-is-desktop'
import { useAppLocale } from '../hooks/ui'

interface DiscoveryHeaderProps {
  cityLabel: string
  onOpenCity: () => void
  unreadCount: number
}

/**
 * Mobile discovery header: logo, city control and notifications only. Saved
 * and create live in the bottom navigation (and the desktop rail), so the
 * header never repeats them. Desktop keeps only the bookmarks shortcut.
 */
export function DiscoveryHeader({
  cityLabel,
  onOpenCity,
  unreadCount,
}: DiscoveryHeaderProps) {
  const { t } = useTranslation()
  const locale = useAppLocale()
  const isDesktop = useIsDesktop()

  return (
    <header className="flex items-center gap-2 py-2 md:py-3">
      <Link
        to="/home"
        className="mr-auto inline-flex min-h-11 shrink-0 items-center"
        aria-label={t('common.appName')}
      >
        <KuniriLogo height={29} className="!h-[29px] w-auto md:!h-[36px]" />
      </Link>

      <button
        type="button"
        onClick={onOpenCity}
        aria-label={t('discovery.city.choose')}
        className="inline-flex min-h-11 min-w-0 max-w-[205px] items-center gap-1.5 rounded-xl px-1 text-[15px] font-bold text-deep hover:bg-mint"
      >
        <MapPin size={19} weight="bold" aria-hidden="true" className="shrink-0 text-teal" />
        <span className="truncate">{cityLabel}</span>
        <CaretDown size={16} weight="bold" aria-hidden="true" className="shrink-0 text-muted" />
      </button>

      {isDesktop ? (
        <Link
          to="/bookmarks"
          className="grid size-11 place-items-center rounded-full text-deep hover:bg-mint"
        >
          <Heart size={22} weight="bold" aria-hidden="true" />
          <span className="sr-only">{t('discovery.header.bookmarks')}</span>
        </Link>
      ) : null}

      <Link
        to="/notifications"
        className="relative grid size-11 shrink-0 place-items-center rounded-full text-deep hover:bg-mint"
        aria-label={
          unreadCount > 0
            ? t('discovery.header.notificationsWithCount', {
                count: formatNumber(unreadCount, locale),
              })
            : t('discovery.header.notifications')
        }
      >
        <Bell size={22} weight="bold" aria-hidden="true" />
        {unreadCount > 0 ? (
          <span className="absolute top-1 right-1 grid min-w-5 place-items-center rounded-full bg-mint px-1 text-[11px] font-bold text-teal">
            {unreadCount > 99
              ? t('discovery.header.overflow', { count: '99' })
              : formatNumber(unreadCount, locale)}
          </span>
        ) : null}
      </Link>

    </header>
  )
}
